import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPromptState =
  | { kind: "android-ready"; prompt: () => void }
  | { kind: "ios-guide" }
  | { kind: "unavailable" };

const DISMISS_KEY = "pwa-install-dismissed";

/**
 * Manages PWA install prompt state across Android and iOS.
 *
 * - Android (Chrome/Edge): captures `beforeinstallprompt` and exposes a
 *   `prompt()` function to trigger the native install dialog.
 * - iOS (Safari): `beforeinstallprompt` is not supported; exposes the
 *   `ios-guide` state so the UI can show manual "Share → Add to Home Screen"
 *   instructions instead.
 * - Already installed (standalone mode) or dismissed this session: `unavailable`.
 */
export function useInstallPrompt(): InstallPromptState {
  const [state, setState] = useState<InstallPromptState>(deriveInitialState);

  useEffect(function listenForInstallEvents() {
    if (isStandaloneMode() || isSessionDismissed()) {
      setState({ kind: "unavailable" });
      return;
    }

    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault();

      const installEvent = event as BeforeInstallPromptEvent;

      setState({
        kind: "android-ready",
        prompt: async () => {
          EventLogger.click(CLIENT_EVENT.PWA_INSTALL_CLICK, { platform: "android" });

          await installEvent.prompt();
          const { outcome } = await installEvent.userChoice;

          EventLogger.result(CLIENT_EVENT.PWA_INSTALL_RESULT, { platform: "android", outcome });

          if (outcome === "accepted") {
            setState({ kind: "unavailable" });
          }
        },
      });
    }

    function handleAppInstalled() {
      EventLogger.track(CLIENT_EVENT.PWA_INSTALLED);
      setState({ kind: "unavailable" });
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return function cleanup() {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  return state;
}

export function dismissInstallPrompt() {
  sessionStorage.setItem(DISMISS_KEY, "true");
}

function deriveInitialState(): InstallPromptState {
  if (isStandaloneMode() || isSessionDismissed()) {
    return { kind: "unavailable" };
  }

  if (isIOSSafari()) {
    return { kind: "ios-guide" };
  }

  return { kind: "unavailable" };
}

/**
 * Check if the browser is in standalone mode.
 * @returns true if the browser is in standalone mode
 */
function isStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isIOS && isSafari;
}

function isSessionDismissed(): boolean {
  return sessionStorage.getItem(DISMISS_KEY) === "true";
}
